import React, { useState } from 'react';
import { Truck, Package, DollarSign, Stars as Stairs, Building, Info } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface PickupRequest {
  id: string;
  user_id: string;
  item_name: string;
  item_description: string;
  pickup_location: string;
  pickup_details: string;
  dropoff_location: string;
  dropoff_details: string;
  payment_offer: number;
  created_at: string;
  status: 'pending' | 'accepted' | 'completed';
}

export function FurniturePickupPage() {
  const [formData, setFormData] = useState({
    itemName: '',
    itemDescription: '',
    pickupLocation: '',
    pickupDetails: '',
    dropoffLocation: '',
    dropoffDetails: '',
    paymentOffer: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Please sign in to submit a request');

      const { error } = await supabase
        .from('pickup_requests')
        .insert({
          user_id: user.id,
          item_name: formData.itemName,
          item_description: formData.itemDescription,
          pickup_location: formData.pickupLocation,
          pickup_details: formData.pickupDetails,
          dropoff_location: formData.dropoffLocation,
          dropoff_details: formData.dropoffDetails,
          payment_offer: parseFloat(formData.paymentOffer),
          status: 'pending'
        });

      if (error) throw error;
      setSuccess(true);
      setFormData({
        itemName: '',
        itemDescription: '',
        pickupLocation: '',
        pickupDetails: '',
        dropoffLocation: '',
        dropoffDetails: '',
        paymentOffer: ''
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-8">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <Truck className="w-8 h-8 text-primary-400" />
          <h1 className="text-2xl font-bold">Furniture Pickup Request</h1>
        </div>

        {success ? (
          <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6">
            Your pickup request has been submitted successfully! We'll notify you when someone accepts the request.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Item Name
              </label>
              <input
                type="text"
                value={formData.itemName}
                onChange={(e) => setFormData(prev => ({ ...prev, itemName: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="e.g., IKEA MALM Dresser"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Item Description
              </label>
              <textarea
                value={formData.itemDescription}
                onChange={(e) => setFormData(prev => ({ ...prev, itemDescription: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md"
                rows={3}
                placeholder="Please provide details about the item's condition, dimensions, and any special handling requirements"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pickup Location
                </label>
                <input
                  type="text"
                  value={formData.pickupLocation}
                  onChange={(e) => setFormData(prev => ({ ...prev, pickupLocation: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Full address"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dropoff Location
                </label>
                <input
                  type="text"
                  value={formData.dropoffLocation}
                  onChange={(e) => setFormData(prev => ({ ...prev, dropoffLocation: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Full address"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pickup Details
                </label>
                <select
                  value={formData.pickupDetails}
                  onChange={(e) => setFormData(prev => ({ ...prev, pickupDetails: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                >
                  <option value="">Select pickup conditions</option>
                  <option value="ground_floor">Ground Floor</option>
                  <option value="elevator">Elevator Available</option>
                  <option value="walk_up_1">1st Floor Walk-up</option>
                  <option value="walk_up_2">2nd Floor Walk-up</option>
                  <option value="walk_up_3">3rd Floor Walk-up</option>
                  <option value="walk_up_4">4th Floor Walk-up</option>
                  <option value="walk_up_5">5th Floor+ Walk-up</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dropoff Details
                </label>
                <select
                  value={formData.dropoffDetails}
                  onChange={(e) => setFormData(prev => ({ ...prev, dropoffDetails: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                >
                  <option value="">Select dropoff conditions</option>
                  <option value="ground_floor">Ground Floor</option>
                  <option value="elevator">Elevator Available</option>
                  <option value="walk_up_1">1st Floor Walk-up</option>
                  <option value="walk_up_2">2nd Floor Walk-up</option>
                  <option value="walk_up_3">3rd Floor Walk-up</option>
                  <option value="walk_up_4">4th Floor Walk-up</option>
                  <option value="walk_up_5">5th Floor+ Walk-up</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Offer ($)
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="number"
                  value={formData.paymentOffer}
                  onChange={(e) => setFormData(prev => ({ ...prev, paymentOffer: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2 border rounded-md"
                  placeholder="Enter amount you're willing to pay"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">Important Notes:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Payment is made directly to the helper upon successful delivery</li>
                  <li>Make sure to accurately describe any stairs or elevator access</li>
                  <li>Include any parking restrictions or building access requirements</li>
                  <li>Higher payment offers typically receive faster responses</li>
                </ul>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="animate-spin">⌛</span>
                  Submitting...
                </>
              ) : (
                <>
                  <Truck className="w-5 h-5" />
                  Submit Pickup Request
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}